import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, ReactNode } from 'react';
import { ApiError, uploadFile } from '../lib/api';
import { classifyFile } from '../lib/files';
import { copyToClipboard, formatBytes } from '../lib/format';
import { formatPixelsAndRatio, readImageDimensions } from '../lib/dimensions';
import type { FileKind, UploadResult } from '../types';
import { FILE_KIND_LABELS } from '../types';
import { Spinner } from './Spinner';
import { FileIcon } from './FileIcon';

const MAX_UPLOAD_MIB = 95;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MIB * 1024 * 1024;
const FALLBACK_PREFIX = 'uploads';
const PARALLELISM = 3;

type ItemStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled' | 'too-large';

interface UploadItem {
  id: string;
  file: File;
  kind: FileKind;
  previewUrl: string | null;
  status: ItemStatus;
  progress: number;
  result: UploadResult | null;
  error: string | null;
  controller: AbortController | null;
  filenameOverride: string;
}

interface UploaderProps {
  defaultPrefix?: string;
  onItemUploaded?: (result: UploadResult) => void;
  onBatchComplete?: (summary: { ok: number; failed: number }) => void;
  onError: (message: string) => void;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeItem(file: File): UploadItem {
  const kind = classifyFile(file.name, file.type);
  const previewUrl = kind === 'image' ? URL.createObjectURL(file) : null;
  const tooLarge = file.size > MAX_UPLOAD_BYTES;
  return {
    id: newId(),
    file,
    kind,
    previewUrl,
    status: tooLarge ? 'too-large' : 'queued',
    progress: 0,
    result: null,
    error: tooLarge ? `Too large (max ${MAX_UPLOAD_MIB} MiB).` : null,
    controller: null,
    filenameOverride: '',
  };
}

// Mirror the server's resolveFilename behaviour closely enough for a live
// preview. Empty override → original; override with extension → as typed;
// override without extension → append original's extension.
function computeFinalName(override: string, originalName: string): string {
  const trimmed = override.trim();
  if (!trimmed) return originalName;
  if (trimmed.includes('.')) return trimmed;
  const idx = originalName.lastIndexOf('.');
  const ext = idx > 0 && idx < originalName.length - 1 ? originalName.slice(idx + 1) : '';
  return ext ? `${trimmed}.${ext}` : trimmed;
}

export function Uploader({ defaultPrefix, onItemUploaded, onBatchComplete, onError }: UploaderProps) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [prefix, setPrefix] = useState(defaultPrefix || FALLBACK_PREFIX);
  const [overwrite, setOverwrite] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync prefix to the active gallery folder when it changes — but only if
  // the user hasn't typed a custom value (we treat the field as untouched
  // while it matches the previously-applied default).
  const lastAppliedDefault = useRef(defaultPrefix || FALLBACK_PREFIX);
  useEffect(() => {
    const next = defaultPrefix || FALLBACK_PREFIX;
    setPrefix((current) => (current === lastAppliedDefault.current ? next : current));
    lastAppliedDefault.current = next;
  }, [defaultPrefix]);

  const itemsRef = useRef<UploadItem[]>([]);
  useEffect(() => {
    const removed = itemsRef.current.filter((prev) => !items.some((cur) => cur.id === prev.id));
    for (const r of removed) if (r.previewUrl) URL.revokeObjectURL(r.previewUrl);
    itemsRef.current = items;
  }, [items]);
  useEffect(
    () => () => {
      for (const it of itemsRef.current) if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    },
    [],
  );

  const updateItem = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback((incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const arr = incoming instanceof FileList ? Array.from(incoming) : Array.from(incoming);
    if (arr.length === 0) return;
    setItems((prev) => [...prev, ...arr.map(makeItem)]);
  }, []);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer?.files ?? null);
  };

  const removeItem = useCallback((id: string) => {
    const target = itemsRef.current.find((it) => it.id === id);
    if (target?.controller && target.status === 'uploading') target.controller.abort();
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const setOverride = useCallback(
    (id: string, value: string) => {
      updateItem(id, { filenameOverride: value });
    },
    [updateItem],
  );

  const clearCompleted = useCallback(() => {
    setItems((prev) =>
      prev.filter(
        (it) =>
          it.status !== 'done' &&
          it.status !== 'cancelled' &&
          it.status !== 'too-large' &&
          it.status !== 'error',
      ),
    );
  }, []);

  const requeueErrored = useCallback(() => {
    setItems((prev) =>
      prev.map((it) =>
        it.status === 'error' || it.status === 'cancelled'
          ? { ...it, status: 'queued', progress: 0, error: null, result: null }
          : it,
      ),
    );
  }, []);

  const uploadOne = useCallback(
    async (item: UploadItem): Promise<'ok' | 'fail'> => {
      const controller = new AbortController();
      updateItem(item.id, { status: 'uploading', progress: 0, error: null, controller });
      try {
        const result = await uploadFile({
          file: item.file,
          prefix,
          filename: item.filenameOverride.trim() || undefined,
          overwrite,
          signal: controller.signal,
          onProgress: (sent, total) => {
            if (total > 0) {
              const pct = Math.min(100, Math.round((sent / total) * 100));
              updateItem(item.id, { progress: pct });
            }
          },
        });
        updateItem(item.id, { status: 'done', progress: 100, result, controller: null });
        onItemUploaded?.(result);
        return 'ok';
      } catch (err) {
        if (controller.signal.aborted) {
          updateItem(item.id, { status: 'cancelled', controller: null, error: 'Cancelled.' });
        } else {
          const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Upload failed.';
          updateItem(item.id, { status: 'error', controller: null, error: message });
          onError(message);
        }
        return 'fail';
      }
    },
    [prefix, overwrite, updateItem, onItemUploaded, onError],
  );

  const startBatch = useCallback(async () => {
    if (batchRunning) return;
    const queued = itemsRef.current.filter((it) => it.status === 'queued');
    if (queued.length === 0) return;

    setBatchRunning(true);

    let okCount = 0;
    let failCount = 0;
    const work = [...queued];
    let cursor = 0;

    const worker = async () => {
      while (true) {
        const idx = cursor++;
        if (idx >= work.length) return;
        const item = work[idx]!;
        const outcome = await uploadOne(item);
        if (outcome === 'ok') okCount++;
        else failCount++;
      }
    };

    const workers = Array.from({ length: Math.min(PARALLELISM, work.length) }, () => worker());
    try {
      await Promise.all(workers);
    } finally {
      setBatchRunning(false);
      onBatchComplete?.({ ok: okCount, failed: failCount });
    }
  }, [batchRunning, uploadOne, onBatchComplete]);

  const queuedCount = useMemo(() => items.filter((it) => it.status === 'queued').length, [items]);
  const doneCount = useMemo(() => items.filter((it) => it.status === 'done').length, [items]);
  const erroredCount = useMemo(
    () => items.filter((it) => it.status === 'error' || it.status === 'cancelled').length,
    [items],
  );
  const hasItems = items.length > 0;

  return (
    <section className="card space-y-5">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Upload</h2>
          <p className="text-xs text-muted">
            Any file type, multi-select OK — up to {MAX_UPLOAD_MIB} MiB each. Folder defaults to{' '}
            <code className="font-mono">{prefix || FALLBACK_PREFIX}</code>.
          </p>
        </div>
        {hasItems && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted">
            {queuedCount > 0 && <span className="pill">{queuedCount} queued</span>}
            {doneCount > 0 && <span className="pill">{doneCount} done</span>}
            {erroredCount > 0 && <span className="pill" style={{ color: '#fda4af' }}>{erroredCount} failed</span>}
          </div>
        )}
      </header>

      <div
        role="button"
        tabIndex={0}
        onClick={() => !batchRunning && fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !batchRunning) {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`relative cursor-pointer overflow-hidden rounded-xl border-2 border-dashed p-6 text-center transition ${
          dragging ? 'border-amber-400 bg-amber-500/5' : 'border-app-border hover:border-app-muted'
        } ${batchRunning ? 'pointer-events-none opacity-80' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="*/*"
          multiple
          className="hidden"
          onChange={handleInputChange}
        />
        <div className="space-y-2">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-app-surface-2 text-muted">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <p className="text-sm">
            Drag &amp; drop files (multiple OK), or <span className="text-amber-300 underline">click to browse</span>.
          </p>
          <p className="text-xs text-muted">
            Images preview inline; everything else gets a typed file tile.
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label mb-2" htmlFor="prefix">Folder / prefix</label>
          <input
            id="prefix"
            className="field focus-ring font-mono"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            disabled={batchRunning}
            placeholder={FALLBACK_PREFIX}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <p className="mt-1 text-[11px] text-muted">All queued files upload into this folder.</p>
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={batchRunning}
            />
            Overwrite if a key already exists
          </label>
        </div>
      </div>

      {hasItems && (
        <ul className="space-y-2">
          {items.map((item) => (
            <UploadRow key={item.id} item={item} onRemove={removeItem} onSetOverride={setOverride} />
          ))}
        </ul>
      )}

      {hasItems && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {erroredCount > 0 && !batchRunning && (
              <button type="button" className="btn btn-ghost text-xs" onClick={requeueErrored}>
                Retry failed
              </button>
            )}
            {(doneCount > 0 || erroredCount > 0) && !batchRunning && (
              <button type="button" className="btn btn-ghost text-xs" onClick={clearCompleted}>
                Clear finished
              </button>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={startBatch}
            disabled={batchRunning || queuedCount === 0}
          >
            {batchRunning ? <Spinner size={14} /> : null}
            <span>
              {batchRunning
                ? 'Uploading…'
                : queuedCount === 0
                  ? 'Nothing queued'
                  : `Upload ${queuedCount} file${queuedCount === 1 ? '' : 's'}`}
            </span>
          </button>
        </div>
      )}
    </section>
  );
}

interface IconButtonProps {
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
  variant?: 'ghost' | 'danger' | 'primary';
  title?: string;
}

function IconButton({ onClick, ariaLabel, children, variant = 'ghost', title }: IconButtonProps) {
  const styles =
    variant === 'danger'
      ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:border-rose-400'
      : variant === 'primary'
        ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-400'
        : 'border-app-border text-muted hover:text-app hover:border-app-muted';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title ?? ariaLabel}
      className={`grid h-8 w-8 flex-none place-items-center rounded-md border transition focus-ring ${styles}`}
    >
      {children}
    </button>
  );
}

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 6 18 18" />
    <path d="M18 6 6 18" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface UploadRowProps {
  item: UploadItem;
  onRemove: (id: string) => void;
  onSetOverride: (id: string, value: string) => void;
}

function UploadRow({ item, onRemove, onSetOverride }: UploadRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const finalName = computeFinalName(item.filenameOverride, item.file.name);
  const isImage = item.kind === 'image';
  const canEdit = item.status === 'queued' || item.status === 'too-large';

  useEffect(() => {
    if (editing) editInputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setDraft(item.filenameOverride);
    setEditing(true);
  };
  const saveEdit = () => {
    onSetOverride(item.id, draft.trim());
    setEditing(false);
  };
  const cancelEdit = () => {
    setDraft('');
    setEditing(false);
  };

  const handleCopy = async () => {
    if (!item.result) return;
    const ok = await copyToClipboard(item.result.url);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  };

  const statusText =
    item.status === 'queued'
      ? 'Queued'
      : item.status === 'uploading'
        ? `Uploading · ${item.progress}%`
        : item.status === 'done'
          ? 'Uploaded'
          : item.status === 'cancelled'
            ? 'Cancelled'
            : item.status === 'too-large'
              ? 'Too large'
              : 'Failed';
  const statusColor =
    item.status === 'done'
      ? 'text-emerald-300'
      : item.status === 'error' || item.status === 'too-large'
        ? 'text-rose-300'
        : item.status === 'cancelled'
          ? 'text-amber-300'
          : 'text-muted';

  const livePreviewName = computeFinalName(draft, item.file.name);
  const renamed = item.filenameOverride.trim().length > 0;

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-app-border bg-app-surface-2 p-2 sm:flex-row sm:items-center">
      <div className="flex h-12 w-12 flex-none overflow-hidden rounded-md border border-app-border bg-black/40">
        {isImage && item.previewUrl ? (
          <img
            src={item.previewUrl}
            alt={item.file.name}
            className="h-full w-full object-cover"
            onLoad={(e) => setDimensions(readImageDimensions(e.currentTarget))}
          />
        ) : (
          <div className="grid h-full w-full place-items-center">
            <FileIcon kind={item.kind} filename={item.file.name} size={32} className="h-9 w-9" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={editInputRef}
              className="field focus-ring font-mono text-xs"
              style={{ flex: '1 1 12rem' }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              placeholder={item.file.name}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              aria-label="Rename file"
            />
            <span className="truncate text-[11px] text-muted">→ {livePreviewName}</span>
          </div>
        ) : (
          <p className="truncate text-sm" title={item.file.name}>
            {finalName}
            {renamed && (
              <span className="ml-2 text-[11px] text-muted" title={`Original: ${item.file.name}`}>
                (renamed)
              </span>
            )}
          </p>
        )}
        <p className="truncate text-[11px] text-muted">
          <span>{formatBytes(item.file.size)}</span>
          <span className="mx-1">·</span>
          <span>{FILE_KIND_LABELS[item.kind]}</span>
          {dimensions && (
            <>
              <span className="mx-1">·</span>
              <span>{formatPixelsAndRatio(dimensions.width, dimensions.height)}</span>
            </>
          )}
          <span className="mx-1">·</span>
          <span className={statusColor}>{statusText}</span>
          {item.error ? (
            <>
              <span className="mx-1">·</span>
              <span className="text-rose-300" title={item.error}>{item.error}</span>
            </>
          ) : null}
        </p>
        {item.status === 'uploading' && (
          <div className="mt-1.5 overflow-hidden rounded-full bg-black/30">
            <div
              className="h-1 transition-[width]"
              style={{
                width: `${item.progress}%`,
                background: 'linear-gradient(90deg, var(--color-app-accent), var(--color-app-accent-2))',
              }}
            />
          </div>
        )}
        {item.status === 'done' && item.result && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <code
              className="max-w-full truncate rounded bg-app-surface px-2 py-0.5 font-mono text-[10px]"
              title={item.result.url}
            >
              {item.result.url}
            </code>
            <button type="button" className="btn btn-ghost text-[10px]" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy URL'}
            </button>
            <a
              href={item.result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost text-[10px]"
            >
              Open
            </a>
          </div>
        )}
      </div>

      <div className="flex flex-none items-center gap-1.5 self-end sm:self-center">
        {editing ? (
          <>
            <IconButton onClick={saveEdit} ariaLabel="Save rename" variant="primary" title="Save (Enter)">
              <CheckIcon />
            </IconButton>
            <IconButton onClick={cancelEdit} ariaLabel="Cancel rename" title="Cancel (Esc)">
              <XIcon />
            </IconButton>
          </>
        ) : (
          <>
            {canEdit && (
              <IconButton onClick={startEdit} ariaLabel="Rename file" title="Rename">
                <PencilIcon />
              </IconButton>
            )}
            {item.status === 'uploading' ? (
              <IconButton
                onClick={() => item.controller?.abort()}
                ariaLabel="Cancel upload"
                variant="danger"
                title="Cancel"
              >
                <XIcon />
              </IconButton>
            ) : (
              <IconButton onClick={() => onRemove(item.id)} ariaLabel="Remove from queue" title="Remove">
                <XIcon />
              </IconButton>
            )}
          </>
        )}
      </div>
    </li>
  );
}
